import React, { useEffect } from 'react'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { FILL_HANDLE_SIZE, BLOCK_FILL_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { isColumnLockedByRules, isNonDataRow } from 'components/spreadsheet/helpers/functions'
import { useColumnPositionResolver, useRangeOverlayLayout } from 'components/spreadsheet/hooks/useRangeOverlayLayout'
import { IFillDragState } from 'components/spreadsheet/types'
import { getViewCapabilities } from 'components/spreadsheet/constants/viewCapabilities'

const FillHandle: React.FC = () => {
  const {
    selectedCell,
    selectedCellRange,
    allColumns,
    fillDragState,
    startFillDrag,
    fillDownToData,
    expandedRowsWithGroups,
    treeLeafColumn,
    totalWidth,
    spreadsheetData
  } = useDataContext()

  const resolver = useColumnPositionResolver()

  // Resolve the selection into a col+row range, or null when the handle shouldn't be shown
  // (editing, read-only anchor column, blocked column type, non-data rows, etc.).
  const selectionRange = (() => {
    if (!selectedCell.rowId || selectedCell.editing) return null

    const nonGroupedColumns = allColumns.filter((c) => !c.isGrouped)
    const treeLeafOffset = treeLeafColumn ? 1 : 0

    const anchorColPos = resolver.getPosition(selectedCell.columnId)
    if (anchorColPos === undefined) return null

    const anchorCol =
      anchorColPos === 0 && treeLeafColumn ? treeLeafColumn : nonGroupedColumns[anchorColPos - treeLeafOffset]
    if (!anchorCol) return null

    const isReadOnly =
      isColumnLockedByRules(spreadsheetData.userConfiguration.rowLockSettings, anchorCol.column.publicId) ||
      anchorCol.column.scriptEnabled ||
      anchorCol.column.formulaEnabled ||
      anchorCol.column.isJoined ||
      anchorCol.column.isSynced
    const isBlockedType = BLOCK_FILL_COLUMN_TYPES.includes(anchorCol.column.kind)
    if (isReadOnly || isBlockedType) return null

    let endRowIndex = selectedCell.rowNumber
    if (selectedCellRange.endRowIndex !== -1) {
      endRowIndex = Math.max(selectedCell.rowNumber, selectedCellRange.endRowIndex)
    }

    if (expandedRowsWithGroups.length > 0) {
      if (isNonDataRow(expandedRowsWithGroups, selectedCell.rowNumber)) return null
      if (isNonDataRow(expandedRowsWithGroups, endRowIndex)) return null
    }

    let endColPos = anchorColPos
    if (selectedCellRange.endColumnId) {
      const endPos = resolver.getPosition(selectedCellRange.endColumnId)
      if (endPos !== undefined) endColPos = Math.max(anchorColPos, endPos)
    } else if (selectedCellRange.endColumnIndex !== -1) {
      endColPos = Math.max(anchorColPos, selectedCellRange.endColumnIndex)
    }

    const minColPos = Math.min(anchorColPos, endColPos)
    const maxColPos = Math.max(anchorColPos, endColPos)

    return {
      minColPos,
      maxColPos,
      // The handle only needs the bottom of the selection's last row, so pass a single-row range.
      minRowIndex: endRowIndex,
      maxRowIndex: endRowIndex
    }
  })()

  // Layout of the selection's last-row/full-col span: gives us endRight (handle x), top + height
  // (handle y via top+height), and endColFrozen (whether to sticky-pin the handle).
  const layout = useRangeOverlayLayout(selectionRange)
  const isFillDragging = !!fillDragState

  // Keep the crosshair cursor while dragging the fill handle. The drag is driven by document-level
  // mousemove handlers (not HTML5 drag-and-drop), so without this the cursor reverts to whatever
  // class the element under the pointer carries (cursor-default on cells, cursor-grab on row
  // numbers, etc.) as soon as the pointer leaves the small handle square.
  useEffect(() => {
    if (!isFillDragging) return undefined
    document.body.classList.add('fill-dragging')
    return () => document.body.classList.remove('fill-dragging')
  }, [isFillDragging])

  if (!layout || !selectionRange) return null

  const handleLeft = layout.endRight - FILL_HANDLE_SIZE / 2
  const handleTop = layout.top + layout.height - FILL_HANDLE_SIZE / 2
  const handleFrozen = layout.endColFrozen
  const handleSize = `${FILL_HANDLE_SIZE}px`

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    startFillDrag()
  }

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    fillDownToData()
  }

  if (!getViewCapabilities(spreadsheetData.viewDetails.type).rows.fillHandle) return null

  return (
    <>
      {/* Fill handle square. When frozen, wrap in an absolute container spanning the full
          scrollable width and use `position: sticky` on the square itself — sticky is handled on
          the compositor, so horizontal pinning stays in lockstep with scroll (no rubber-banding).
          When non-frozen, render a plain absolute square that scrolls with content. */}
      {handleFrozen ? (
        <div
          className="absolute pointer-events-none z-[4]"
          style={{
            top: `${handleTop}px`,
            left: 0,
            width: `${totalWidth}px`,
            height: handleSize
          }}
        >
          <div
            data-testid="fill-handle"
            className="sticky bg-blue cursor-crosshair pointer-events-auto"
            style={{
              left: `${handleLeft}px`,
              width: handleSize,
              height: '100%'
            }}
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
          />
        </div>
      ) : (
        <div
          data-testid="fill-handle"
          className="absolute bg-blue cursor-crosshair z-[2]"
          style={{
            left: `${handleLeft}px`,
            top: `${handleTop}px`,
            width: handleSize,
            height: handleSize
          }}
          onMouseDown={handleMouseDown}
          onDoubleClick={handleDoubleClick}
        />
      )}

      {/* Fill preview overlay — reuse the selection's col positions since the drag captures the
          selection at start, so the preview always spans the same columns. FillPreview calls the
          overlay layout hook itself with its own row range. */}
      {fillDragState && (
        <FillPreview
          fillDragState={fillDragState}
          minColPos={selectionRange.minColPos}
          maxColPos={selectionRange.maxColPos}
          totalWidth={totalWidth}
        />
      )}
    </>
  )
}

interface FillPreviewProps {
  fillDragState: IFillDragState
  minColPos: number
  maxColPos: number
  totalWidth: number
}

const FillPreview: React.FC<FillPreviewProps> = ({ fillDragState, minColPos, maxColPos, totalWidth }) => {
  const { sourceStartRowIndex, sourceEndRowIndex, targetRowIndex, direction } = fillDragState

  const previewInput = (() => {
    if (!direction) return null

    const minSourceRow = Math.min(sourceStartRowIndex, sourceEndRowIndex)
    const maxSourceRow = Math.max(sourceStartRowIndex, sourceEndRowIndex)

    let fillStartRow: number
    let fillEndRow: number

    if (direction === 'down') {
      fillStartRow = maxSourceRow + 1
      fillEndRow = targetRowIndex
    } else if (direction === 'up') {
      fillStartRow = targetRowIndex
      fillEndRow = minSourceRow - 1
    } else {
      return null
    }

    if (fillStartRow > fillEndRow) return null

    return { minColPos, maxColPos, minRowIndex: fillStartRow, maxRowIndex: fillEndRow }
  })()

  const layout = useRangeOverlayLayout(previewInput)
  if (!layout) return null

  return (
    <React.Fragment>
      {layout.nonFrozen && (
        <div
          className="absolute border border-dashed border-blue bg-blue/10 pointer-events-none z-[2]"
          style={{
            left: `${layout.nonFrozen.left}px`,
            top: `${layout.top}px`,
            width: `${layout.nonFrozen.width}px`,
            height: `${layout.height}px`
          }}
        />
      )}
      {layout.frozen && (
        <div
          className="absolute pointer-events-none z-[4]"
          style={{
            top: `${layout.top}px`,
            left: 0,
            width: `${totalWidth}px`,
            height: `${layout.height}px`
          }}
        >
          <div
            className={`sticky ${
              layout.frozen.allBorders ? 'border' : 'border-t border-b border-l'
            } border-dashed border-blue bg-blue/10 pointer-events-none`}
            style={{
              left: `${layout.frozen.startLeft}px`,
              width: `${layout.frozen.width}px`,
              height: '100%'
            }}
          />
        </div>
      )}
    </React.Fragment>
  )
}

export default FillHandle
